/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DataModel } from '../../engine/data-model';
import { HttpErrorResponse } from '@angular/common/http';
import { EventEmitter } from '@angular/core';
import { DataModelFilters } from '../../engine/data-model-filters.enum';
import { NameValue } from '../../tool/name-value';
import { DataTableState } from '../../model/data-table/data-table-state';
import { ColumnManager } from './column-manager';
import { ModelProperty } from '../../engine/model-property';
import { GroupEntity } from '../../tool/group-entity';
import { TableEntity } from '../../model/entity/table-entity';
import { map, tap } from 'rxjs/operators';
import { ECalculator } from '../../tool/e-calculator';
import { IEntity } from '../../interface/IEntity';
import { ColumnGroupManager } from './column-group-manager';
import { RestService } from '../../engine/http/rest.service';
import { ObjectFactory } from '../../engine/http/factory/object-factory';
import { ColumnGroup } from './column-group';
import { FormatPipe } from '../../pipe/format.pipe';
import { CommonUtils } from '../../tool/common-utils';
import dayjs from 'dayjs';
import { Format2Pipe } from '../../pipe/format2.pipe';
import { StandardEntity } from '../../engine/standard-entity';

export abstract class TableManager<T extends IEntity> {
  get printableData(): T[] {
    return this._printableData;
  }

  public loadSubject: Subject<T[]> = new Subject<T[]>();
  public changeGroup = new BehaviorSubject<{
    column: ColumnManager;
    open: boolean;
  }>(null);
  public changeFilters: EventEmitter<any> = new EventEmitter();
  public changeTotal = new BehaviorSubject<any>(null);
  public model: DataModel;
  public state = new DataTableState<T>();
  public columns: ColumnManager[];

  public columnGroupManager = new ColumnGroupManager();
  public fullLoad = true;

  public isLoading = new EventEmitter<boolean>();
  public isFlowView = false;
  public displayCount: number;

  public displayColumns: ColumnManager[];
  public displayHeader: ColumnManager[];
  public lastSwitchColumn: ColumnManager;
  private groupMovePosition: {
    draggingGroup: { title: string; columns: ColumnManager[]; width: number };
    position: number;
  };
  public rowTotal: T;
  public totalLoaded = true;

  public currentPage = 0;

  public selectedRow: T;

  public sorter: { column: ModelProperty; indice: 1 | -1 };

  public data: T[]; // Intégralité des datas sur lequel travaille le manager
  protected _printableData: T[]; // Data prévu pour l'impression
  public count: number; // Nombre total de données retourné par le serveur (Ne correspond pas forcément à data.length si chargement paginé

  public hasDisabledRows$ = new EventEmitter();
  public hasSelectedRows$ = new EventEmitter();

  private _error: HttpErrorResponse;

  /**
   * Set true after loadDataSource
   */
  private dataSourcesAreLoaded: boolean;

  public disabledRows: T[];
  public selectedRows: T[];

  private stickyGroups: ColumnGroup[];
  public stickyColumn: ColumnManager;
  private leftStickyColumns: ColumnManager[];

  private shouldApplyDefaultGroup = true;

  protected constructor(model: DataModel, struct?: string[], onGroup = false) {
    this.model = model;
    if (this.hasDefaultSort()) {
      this.sorter = {
        column: this.model.defaultSort.property,
        indice: this.model.defaultSort.sort === 'sort-up' ? 1 : -1,
      };
    }
    this.loadSubject
      .pipe(
        map((entities) => {
          if (!this.sorter) {
            return entities;
          }
          return entities?.sort(
            (a, b) =>
              this.compare(a, b, this.sorter.column) * this.sorter.indice,
          );
        }),
        tap((entities) => {
          this.applyDefaultGroup();
          this.rowTotal = this.getTotal(entities);
        }),
      )
      .subscribe();
    this.convertModelToColumns(struct, onGroup);
  }

  private applyDefaultGroup(): void {
    if (!this.shouldApplyDefaultGroup) {
      return;
    }
    this.shouldApplyDefaultGroup = false;
    if (this.groupProperty) {
      return;
    }
    if (!this.model.defaultGroup) {
      return;
    }
    const defaultGroup = this.getDefaultGroup();
    if (!defaultGroup) {
      console.warn(
        `Impossible to find default group "${this.model.defaultGroup.id}"`,
      );
      return;
    }
    this.groupProperty = defaultGroup;
    this.changeGroup.next({ column: this.groupProperty, open: false });
  }

  public convertModelToColumns(
    struct?: string[],
    onGroup = false,
    multiSelection = false,
    showIndex = false,
  ) {
    const oldColumns = this.columns;
    this.columns = [];
    if (!struct) {
      const properties = this.model.getProperty(DataModelFilters.table);
      try {
        properties.sort((a, b) => a.tablePosition - b.tablePosition);
      } catch (e) {
        console.error(
          `Error while sorting properties for model ${this.model.id}: ${e.message}`,
        );
      }
      for (const property of properties) {
        this.addColumn(property, oldColumns);
      }
    } else {
      const temp = this.model.getProperty(DataModelFilters.table);
      for (const id of struct) {
        const columnIndex = temp.findIndex((c) => {
          return c.id === id;
        });
        if (columnIndex >= 0) {
          const property = temp.splice(columnIndex, 1)[0];
          const c = this.addColumn(property, oldColumns);
          c.showOnTable = true;
        }
      }
      for (const property of temp) {
        const column = new ColumnManager(property);
        column.showOnTable = false;
        this.columns.push(column);
      }
    }

    this.displayHeader = [];
    this.getDisplayHeader(onGroup, multiSelection, showIndex);
  }

  /**
   * Notify a reload to the manager
   * @param forceRefresh is false, the manager can return the storage data if has one
   */
  public notify(forceRefresh?: boolean) {
    this._error = null;
    this.isLoading.emit(true);
    this.load(forceRefresh);
  }

  /**
   * Return the observable of loading data
   */
  public abstract getEntities(): Observable<any[]>;

  /**
   * Load the data on the manager
   * @param forceRefresh is true if data cannot use store data and force refresh its data
   */
  public abstract load(forceRefresh?: boolean);

  set error(value: HttpErrorResponse) {
    this.isLoading.emit(false);
    this._error = value;
  }

  get error(): HttpErrorResponse {
    return this._error;
  }

  /**
   * Get the list of displaying columns
   */
  public getDisplayHeader(
    onGroup = false,
    multiSelection = false,
    index = false,
  ) {
    if (!this.displayHeader) {
      this.displayHeader = [];
    }
    this.displayColumns = [];
    this.columns.forEach((column) => {
      const i = this.displayHeader.findIndex(
        (dh) => dh.property.id === column.property.id,
      );
      if (i >= 0) {
        this.displayColumns.splice(i, 0, column);
        return;
      }
      this.displayColumns.push(column);
    });
    this.displayHeader = this.displayColumns.slice(0);
    if (onGroup) {
      this.generateColumnGroups();
    }
    this.displayCount = this.displayColumns.length;
    if (this.stickyGroups) {
      this.anchorGroup(multiSelection, index);
    }
    return this.displayColumns;
  }

  /**
   * Remove the data on the selected index
   * @param index
   * Return the data removed
   */
  public removeRow(index: number): T {
    const entity = this.data.splice(index, 1);
    this.count = this.data.length;
    return entity.length > 0 ? entity[0] : null;
  }

  public addNewRow(index: number): T | any {
    let entity;
    if (this.model.baseEntity) {
      entity = this.model.baseEntity.convert({});
    } else {
      entity = new TableEntity({});
    }
    if (index && index >= 0 && index < this.data.length) {
      this.data.splice(index, 0, entity);
    } else {
      this.data.push(entity);
    }
    this.count = this.data.length;
    this.notify();
    return entity;
  }

  public addRow(row: any) {
    const factory = new ObjectFactory(
      null,
      this.model.getPrimaryKey(),
      this.model.getMainLibKeys(),
    );
    this.data.push(factory.build(row));
  }

  public updateRow(row: T, data: any) {
    // tslint:disable-next-line:forin
    for (const attr in data) {
      row[attr] = data[attr];
    }
  }

  /**
   * Switch two column on table
   * @param head1 property of column switch
   * @param head2 property of column switch
   */
  public switchColumn(head1: ColumnManager, head2: ColumnManager) {
    if (head2 !== this.lastSwitchColumn && head1 !== head2) {
      this.lastSwitchColumn = head2;
      const position1 = head1.property.tablePosition;
      const position2 = head2.property.tablePosition;
      this.displayHeader = this.displayHeader.map((head, index) => {
        if (head === head1) {
          head2.property.tablePosition = position1 ? position1 : index + 1;
          return head2;
        } else if (head === head2) {
          head1.property.tablePosition = position2 ? position2 : index + 1;
          return head1;
        } else {
          return head;
        }
      });
    }
  }

  public loadDataSource(rest: RestService) {
    if (!this.dataSourcesAreLoaded) {
      this.columns.forEach((c) => {
        c.loadDataSource(rest);
      });
      this.dataSourcesAreLoaded = true;
    }
  }

  /**
   * Move the column to the good position
   */
  moveColumn(draggingHeader: ColumnManager, position: number) {
    const index = this.displayHeader.indexOf(draggingHeader);
    if (index >= 0) {
      this.displayHeader.splice(index, 1);
      this.displayHeader.splice(position, 0, draggingHeader);
    }
  }

  moveGroup(
    draggingGroup: { title: string; columns: ColumnManager[]; width: number },
    position: number,
  ) {
    this.groupMovePosition = { draggingGroup, position };
  }

  /**
   * Fix column after validating switch
   */
  public fixColumns(isGrouped?: boolean) {
    if (isGrouped) {
      this.columnGroupManager.generate(this.displayHeader);
      this.displayColumns = this.columnGroupManager.generateDisplayColumns();
      this.displayHeader = this.displayColumns.slice();
    } else {
      this.displayColumns = this.displayHeader.slice();
      this.lastSwitchColumn = null;
    }
  }

  public hasDefaultSort(): boolean {
    return this.model.defaultSort && this.model.defaultSort.property != null;
  }

  public getDefaultGroup(): ColumnManager {
    if (!this.model.defaultGroup) {
      return null;
    }
    return this.columns.find(
      (c) => c.property.id === this.model.defaultGroup.id,
    );
  }

  public get groupProperty(): ColumnManager {
    return this.state.groupProperty;
  }

  public set groupProperty(property: ColumnManager) {
    if (
      this.state.groupProperty &&
      (!property ||
        property.property.id !== this.state.groupProperty.property.id)
    ) {
      this.state.groupProperty.isGrouped = false;
      this.state.groupProperties = null;
    }
    if (property) {
      property.isGrouped = true;
    }
    this.state.groupProperty = property;
  }

  public set groupEntities(groupEntities: GroupEntity<T>[]) {
    this.state.groupProperties = groupEntities;
    for (const group of groupEntities) {
      group.total = this.getTotal(group.items.map((gr) => gr.entity));
    }
  }

  public get groupEntities(): GroupEntity<T>[] {
    return this.state.groupProperties;
  }

  /**
   * Add or update a column filter
   * @param column manager of column
   * @param filters list of filters
   */
  public setFilter(column: ColumnManager, filters: NameValue[]) {
    column.filters = filters;
    this.state.setFilter(column.property, filters);
  }

  /**
   * Apply filter to the dataTable
   */
  filter(column: ColumnManager, filters: NameValue[]) {
    this.setFilter(column, filters);
    this.applyAllFilters();
  }

  public applyAllFilters() {
    const tempData = [];
    if (this.data) {
      for (const d of this.data) {
        if (this.applyFilter(d)) {
          tempData.push(d);
        }
      }
      this.count = tempData.length;
      this._printableData = tempData;
    }
    this.loadSubject.next(tempData);
    this.changeFilters.emit();
  }

  /**
   * Check if data is compliant with all filters
   */
  protected applyFilter(data: any): boolean {
    for (const f of this.state.filterProperties) {
      if (!f.check(data)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Return if manager contains all printable data
   */
  public hasCompleteData() {
    return true;
  }

  /**
   * Return the observable to load printable data
   */
  public getPrintableData(): Observable<T[]> {
    return null;
  }

  public abstract changePage(page: number, step: number);

  public abstract changeStep(previousValue: number, newValue: number);

  // public abstract filter(column: ColumnManager, filters: NameValue[]);

  public abstract notifySort(data: any[]);

  public sort(column: ModelProperty, indice: 1 | -1) {
    this.sorter = { column: column, indice: indice };
    if (this.printableData) {
      /* istanbul ignore next */
      this.printableData?.sort((a, b) => {
        return this.compare(a, b, column) * indice;
      });
      this.notifySort(this.printableData);
    }
  }

  /**
   * Compare the element in term of ModelProperty type
   * @param column model property
   * @param a first element of comparison
   * @param b second element of comparison
   */
  public compare(a: unknown, b: unknown, column: ModelProperty): number {
    let v1 = a[column.id];
    let v2 = b[column.id];
    if (column.sortByField) {
      v1 = a[column.id][column.sortByField];
      v2 = b[column.id][column.sortByField];
    }
    if (this.isUndefined(v1) && this.isUndefined(v2)) {
      return 0;
    }
    if (this.isUndefined(v1)) {
      return -1;
    }
    if (this.isUndefined(v2)) {
      return 1;
    }
    try {
      switch (column.sortType) {
        case 'date':
          return this.compareDate2(v1, v2);
        case 'dateTime':
          return this.compareDateTime(v1, v2, column);
        case 'time':
          return this.compareTime(v1, v2);
        case 'number':
          return this.compareNumber(v1, v2);
        case 'icon':
          return this.compareIcon(v1, v2);
        case 'standardEntity':
          return this.compareStandardEntity(v1, v2, column, a, b);
        default:
          return this.compareString(v1, v2, column, a, b);
      }
    } catch (e) {
      console.error(v1, v2, e.message);
      return 0;
    }
  }

  private isUndefined(value: unknown): boolean {
    return value === null || value === undefined;
  }

  public compareIcon(a: string, b: string): number {
    return a.localeCompare(b);
  }

  public compareString(
    a: any,
    b: any,
    property: ModelProperty,
    aEntity: any,
    bEntity: any,
  ): number {
    const pipe = new Format2Pipe();
    a = pipe
      .transform(a, property, aEntity, false, true)
      ?.toString()
      .toLowerCase();
    b = pipe
      .transform(b, property, bEntity, false, true)
      ?.toString()
      .toLowerCase();
    return a.localeCompare(b);
  }
  public compareNumber(a: any, b: any): number {
    try {
      if (!(typeof a === 'number')) {
        a = parseFloat(a + '');
      }
      if (!(typeof b === 'number')) {
        b = parseFloat(b + '');
      }
      return a - b;
    } catch (e) {
      console.error(`${e.message} (${a} > ${b}`);
      return 0;
    }
  }
  public compareTime(a: any, b: any): number {
    if (typeof a === 'string') {
      a = CommonUtils.convertStrToTime(a);
    }
    if (typeof b === 'string') {
      b = CommonUtils.convertStrToTime(b);
    }
    return a.hours * 60 + a.minutes - (b.hours * 60 + b.minutes);
  }

  public compareDate2(a: unknown, b: unknown): number {
    const dateA = this.convertToDate(a);
    const dateB = this.convertToDate(b);
    if (dateA && dateB) {
      return dateA.getTime() - dateB.getTime();
    }
    if (!dateA) {
      return -1;
    }
    /* istanbul ignore else */
    if (!dateB) {
      return 1;
    }
    /* istanbul ignore next */
    return 0;
  }

  public compareDateTime(a: unknown, b: unknown, prop: ModelProperty): number {
    const dateA = this.convertToDateTime(a, prop);
    const dateB = this.convertToDateTime(b, prop);
    if (dateA && dateB) {
      return dateA.getTime() - dateB.getTime();
    }
    if (!dateA) {
      return -1;
    }
    /* istanbul ignore else */
    if (!dateB) {
      return 1;
    }
    /* istanbul ignore next */
    return 0;
  }

  private convertToDate(a: unknown): Date {
    if (typeof a === 'string') {
      return CommonUtils.convertStrToDate(a);
    } else if (a instanceof Date) {
      return a;
    } else if (dayjs.isDayjs(a) && a.isValid) {
      return a.toDate();
    }
    return null;
  }

  private convertToDateTime(a: unknown, prop: ModelProperty): Date {
    if (typeof a === 'string') {
      return dayjs(a, prop.format || 'DD/MM/YYYY HH:mm:ss').toDate();
    }
    if (typeof a === 'number') {
      return new Date(a);
    }
    if (a instanceof Date) {
      return a;
    }
    if (dayjs.isDayjs(a) && a.isValid) {
      return a.toDate();
    }
    return null;
  }

  public buildProperty(array: any[], name: string, enumArray: string[]) {
    for (let i = 0; i < enumArray.length; ++i) {
      if (i >= array.length) {
        array.push({});
      }
      array[i][name] = enumArray[i];
    }
  }

  public clear() {
    this.data = [];
    this._printableData = [];
    this.count = 0;
    this.currentPage = 1;
    this.sorter = null;
    this.notify();
  }

  /**
   * Add a new column to the table
   * @param property not null
   */
  // public addColumn(property: ModelProperty): boolean {
  //   // TODO implement something
  //   if (this.columns.find((c) => c.property.id === property.id)) {
  //     return false;
  //   }
  //   this.columns.push(new ColumnManager(property));
  //   this.getDisplayHeader();
  //   return true;
  // }
  public clearFilters() {
    this.state.clearFilters();
    for (const c of this.columns) {
      if (c.filters && c.filters.length > 0) {
        c.filters = [];
        this.filter(c, []);
      }
    }
  }

  getFilters(): ColumnManager[] {
    const array = [];
    for (const c of this.columns) {
      if (c.filters && c.filters.length > 0) {
        array.push(c);
      }
    }
    return array;
  }

  /**
   * Create the total row of the line
   */
  getTotal(entities: any[]): T {
    return new ECalculator().sum(this.model, entities);
  }

  private generateColumnGroups() {
    this.columnGroupManager.reset();
    for (const c of this.displayHeader) {
      this.columnGroupManager.add(c);
    }
    if (this.stickyGroups) {
      this.stickyGroups = this.columnGroupManager.findGroups(
        this.stickyGroups.map((sg) => sg.title),
      );
    }
    this.columnGroupManager.refreshWidth();
    this.generateDisplayHeaderByGroup();
  }

  public generateDisplayHeaderByGroup() {
    this.displayColumns = this.columnGroupManager.generateDisplayColumns();
    this.displayHeader = this.displayColumns.slice();
  }

  fixGroups() {
    this.columnGroupManager.move(
      this.groupMovePosition.draggingGroup.title,
      this.groupMovePosition.position,
    );
  }

  /**
   * Disabled rows on data
   */
  public disableRows(rows: T[]) {
    this.disabledRows = rows;
    this.hasDisabledRows$.emit();
  }

  public fixColumnGroup(groups: ColumnGroup[]) {
    this.stickyGroups = groups;
  }

  public groupBy(property: string, openGroups = false) {
    this.groupProperty = this.columns.find((c) => c.property.id === property);
    this.changeGroup.next({ column: this.groupProperty, open: openGroups });
  }

  anchorColumn(column: ColumnManager, multiSelection = false, index = false) {
    if (this.stickyGroups) {
      this.stickyGroups.forEach((sg) => sg.unfix());
    }
    if (this.leftStickyColumns) {
      this.leftStickyColumns.forEach((lsc) => (lsc.isFixed = false));
    }
    this.leftStickyColumns = this.addStickyColumn(column);
    this.setFixLeftOffset(multiSelection, index);
  }

  private addStickyColumn(column: ColumnManager): ColumnManager[] {
    if (!column) {
      return null;
    }
    if (column === this.stickyColumn) {
      column.isFixed = false;
      this.stickyColumn = null;
      return null;
    } else {
      const array = [];
      this.stickyColumn = column;
      for (const dc of this.displayColumns) {
        if (dc !== column) {
          array.push(dc);
        } else {
          array.push(column);
          return array;
        }
      }
    }
    return null;
  }

  public setFixLeftOffset(multiSelection: boolean, index: boolean) {
    let offset = 0;
    if (this.leftStickyColumns) {
      if (multiSelection) {
        offset += 25;
      }
      if (index) {
        offset += 30;
      }
      for (const c of this.leftStickyColumns) {
        if (c.showOnTable) {
          c.fix(offset);
          offset += c.width;
        }
      }
    }
    /* istanbul ignore else */
    if (this.columnGroupManager) {
      this.columnGroupManager.refreshFixedColumn(multiSelection, index);
    }
  }

  refreshAnchor(multiSelection: boolean, index: boolean) {
    const temp = this.stickyColumn;
    this.stickyColumn = null;
    this.anchorColumn(temp, multiSelection, index);
  }

  public clearStickyGroups() {
    this.stickyGroups = null;
  }

  private anchorGroup(multiSelection: boolean, index: boolean) {
    const lastColumns = this.stickyGroups.map((gr) => {
      const array = gr.columns.filter((c) => c.showOnTable);
      return array[array.length - 1];
    });
    let lc = lastColumns[0];
    let position = this.displayColumns.indexOf(lc);
    for (let i = 1; i < lastColumns.length; ++i) {
      const p = this.displayColumns.indexOf(lastColumns[i]);
      if (p < position) {
        lc = lastColumns[i];
        position = p;
      }
    }
    if (lc) {
      this.anchorColumn(lc, multiSelection, index);
    }
  }

  private compareObject(a: any, b: any, header: ModelProperty) {
    if (typeof a === 'object') {
      if (a instanceof Array) {
        const a1 = FormatPipe.arrayFactory(a, header);
        const b1 = FormatPipe.arrayFactory(b, header);
        return a1.join('').localeCompare(b1.join(''));
      } else {
        const a1 = FormatPipe.objectFactory(a, header);
        const b1 = FormatPipe.objectFactory(b, header);
        return a1.localeCompare(b1);
      }
    } else {
      return (<string>a + '').localeCompare(<string>b + '');
    }
  }

  private compareDate(a: any, b: any): number {
    let response = 0;
    if (a instanceof Date) {
      response = (<Date>a).getTime() - (<Date>b).getTime();
    } else if (a.toDate) {
      response = a.toDate().getTime() - b.toDate().getTime();
    } else if (typeof a === 'string') {
      const d1 = CommonUtils.convertStrToDate(a);
      const d2 = CommonUtils.convertStrToDate(b);
      const t1 = d1 ? d1.getTime() : -1;
      const t2 = d2 ? d2.getTime() : -1;
      response = t1 - t2;
    }
    return response;
  }

  protected addColumn(
    property: ModelProperty,
    oldColumns: ColumnManager[],
  ): ColumnManager {
    const newColumn = new ColumnManager(property);
    this.columns.push(newColumn);
    if (oldColumns) {
      const found = oldColumns.find((oc) => oc.property.id === property.id);
      if (found) {
        newColumn.dataSourceSync = found.dataSourceSync;
        newColumn.filters = found.filters;
      }
    }
    return newColumn;
  }

  public selectRows(rows: T[]) {
    this.selectedRows = rows;
    this.hasSelectedRows$.emit();
  }

  private compareStandardEntity(
    v1: any,
    v2: any,
    property: ModelProperty,
    aEntity: any,
    bEntity: any,
  ) {
    if (v1 instanceof StandardEntity && v2 instanceof StandardEntity) {
      return v1.compare(v2);
    }
    console.error('Not instance of StandardEntity', v1, v2);
    return this.compareString(v1, v2, property, aEntity, bEntity);
  }
}
